import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { statusColor } from "./Utils/Utilities";

// --- Badge ---
function Badge({ status }) {
  const color = statusColor(status);
  return (
    <span
      className="badge"
      style={{ color, borderColor: `${color}30`, background: `${color}14` }}
    >
      {status}
    </span>
  );
}

// --- Toast System ---
let toastId = 0;
let setToastsGlobal = null;
const showToast = (msg, type = "s") => {
  if (!setToastsGlobal) return;
  const id = ++toastId;
  setToastsGlobal((p) => [...p, { id, msg, type }]);
  setTimeout(() => setToastsGlobal((p) => p.filter((t) => t.id !== id)), 3500);
};

function Toasts() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    setToastsGlobal = setToasts;
    return () => {
      setToastsGlobal = null;
    };
  }, []);
  const icons = {
    s: <i className="bi bi-check2" />,
    e: <i className="bi bi-x" />,
    w: <i className="bi bi-exclamation-triangle" />,
  };
  return (
    <div className="toasts">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.type}`}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>{icons[t.type]}</span>
          <span>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

// --- Modal ---
function Modal({ title, onClose, children, footer, size }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div
      className="overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`modal ${size === "lg" ? "modal-lg" : ""}`}>
        <div className="mhdr">
          <span className="mtitle">{title}</span>
          <button className="btn btn-g btn-ico btn-sm" onClick={onClose}>
            BACK
          </button>
        </div>
        <div className="mbody">{children}</div>
        {footer && <div className="mfoot">{footer}</div>}
      </div>
    </div>
  );
}

// --- Confirm Dialog ---
function Confirm({ message, onConfirm, onCancel }) {
  return (
    <Modal
      title="Confirm Action"
      onClose={onCancel}
      footer={
        <>
          <button className="btn btn-g" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-d" onClick={onConfirm}>
            Confirm Delete
          </button>
        </>
      }
    >
      <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>
        {message}
      </p>
    </Modal>
  );
}

// --- Revenue SVG Chart ---
function RevenueChart({ data, height = 180 }) {
  const [tip, setTip] = useState(null);
  const W = 640,
    H = height;
  const pad = { t: 20, r: 20, b: 36, l: 48 };
  const iw = W - pad.l - pad.r,
    ih = H - pad.t - pad.b;
  const maxV = Math.max(...data.map((d) => Math.max(d.revenue, d.target)));
  const x = (i) => pad.l + (i / (data.length - 1)) * iw;
  const y = (v) => pad.t + ih - (v / maxV) * ih;
  const revLine = data
    .map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(d.revenue)}`)
    .join(" ");
  const tgtLine = data
    .map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(d.target)}`)
    .join(" ");
  const areaPath = `${revLine} L ${x(data.length - 1)} ${H - pad.b} L ${x(0)} ${H - pad.b} Z`;
  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="chart-area" style={{ height }}>
        <defs>
          <linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--flame)" stopOpacity=".3" />
            <stop offset="100%" stopColor="var(--flame)" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="yg" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--flame)" />
            <stop offset="100%" stopColor="var(--amber)" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const yp = pad.t + ih * (1 - t);
          return (
            <g key={t}>
              <line
                x1={pad.l}
                y1={yp}
                x2={W - pad.r}
                y2={yp}
                stroke="var(--border)"
                strokeWidth="1"
              />
              <text
                x={pad.l - 6}
                y={yp + 4}
                textAnchor="end"
                fill="var(--text3)"
                fontSize="9"
                fontFamily="'JetBrains Mono',monospace"
              >
                {Math.round(maxV * t)}
              </text>
            </g>
          );
        })}
        <path d={areaPath} fill="url(#rg)" />
        <path
          d={tgtLine}
          stroke="var(--amber)"
          strokeWidth="1.5"
          strokeDasharray="5 4"
          fill="none"
          opacity=".6"
        />
        <path
          d={revLine}
          stroke="url(#yg)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {data.map((d, i) => (
          <circle
            key={i}
            cx={x(i)}
            cy={y(d.revenue)}
            r="4"
            fill="var(--flame)"
            stroke="var(--surface)"
            strokeWidth="2"
            style={{ cursor: "pointer" }}
            onMouseEnter={(e) => setTip({ d, i, mx: e.clientX, my: e.clientY })}
            onMouseLeave={() => setTip(null)}
          />
        ))}
        {data.map((d, i) => (
          <text
            key={i}
            x={x(i)}
            y={H - pad.b + 14}
            textAnchor="middle"
            fill="var(--text3)"
            fontSize="9"
            fontFamily="'JetBrains Mono',monospace"
          >
            {d.month}
          </text>
        ))}
      </svg>
      {tip && (
        <div
          className="chart-tip"
          style={{ left: tip.mx + 12, top: tip.my - 40, position: "fixed" }}
        >
          <div style={{ fontWeight: 700, marginBottom: 3 }}>{tip.d.month}</div>
          <div style={{ color: "var(--flame)" }}>
            Revenue: ${tip.d.revenue}k
          </div>
          <div style={{ color: "var(--amber)" }}>Target: ${tip.d.target}k</div>
        </div>
      )}
    </div>
  );
}

// --- Bar Chart ---
function BarChart({
  data,
  keyVal,
  keyLabel,
  color = "var(--flame)",
  height = 140,
}) {
  const maxV = Math.max(...data.map((d) => d[keyVal]));
  const W = 400,
    H = height;
  const pad = { t: 10, r: 10, b: 28, l: 10 };
  const bw = (W - pad.l - pad.r - (data.length - 1) * 8) / data.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart-area" style={{ height }}>
      {data.map((d, i) => {
        const bh = (d[keyVal] / maxV) * (H - pad.t - pad.b);
        const bx = pad.l + i * (bw + 8);
        const by = H - pad.b - bh;
        return (
          <g key={i}>
            <rect
              x={bx}
              y={by}
              width={bw}
              height={bh}
              rx="3"
              fill={color}
              opacity=".8"
            />
            <rect x={bx} y={by} width={bw} height={3} rx="2" fill={color} />
            <text
              x={bx + bw / 2}
              y={H - pad.b + 12}
              textAnchor="middle"
              fill="var(--text3)"
              fontSize="9"
              fontFamily="'JetBrains Mono',monospace"
            >
              {d[keyLabel]}
            </text>
            <text
              x={bx + bw / 2}
              y={by - 4}
              textAnchor="middle"
              fill="var(--text2)"
              fontSize="9"
              fontFamily="'JetBrains Mono',monospace"
            >
              {d[keyVal]}%
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// --- Donut Chart ---
function DonutChart({ data, size = 120 }) {
  const colors = [
    "var(--flame)",
    "var(--amber)",
    "var(--blue)",
    "var(--teal)",
    "var(--success)",
  ];
  const total = data.reduce((s, d) => s + d.value, 0);
  let cumulative = 0;
  const r = 40,
    cx = size / 2,
    cy = size / 2,
    strokeW = 14;
  const slices = data.map((d, i) => {
    const pct = d.value / total;
    const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    const x1 = cx + r * Math.cos(startAngle),
      y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle),
      y2 = cy + r * Math.sin(endAngle);
    const large = pct > 0.5 ? 1 : 0;
    return {
      path: `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`,
      color: colors[i % colors.length],
      d,
    };
  });
  return (
    <div className="flex gap16">
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="var(--border)"
          strokeWidth={strokeW}
        />
        {slices.map((s, i) => (
          <path
            key={i}
            d={s.path}
            fill="none"
            stroke={s.color}
            strokeWidth={strokeW}
            strokeLinecap="butt"
          />
        ))}
      </svg>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 6,
        }}
      >
        {data.map((d, i) => (
          <div key={i} className="fc gap8 fs11">
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: colors[i % colors.length],
                flexShrink: 0,
              }}
            />
            <span style={{ flex: 1, color: "var(--text2)" }}>{d.product}</span>
            <span className="mono fw7">{d.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Collection Bar Chart ---
function CollectionBarChart({ data, height = 160 }) {
  const [tip, setTip] = useState(null);
  const W = 640,
    H = height;
  const pad = { t: 16, r: 16, b: 28, l: 40 };
  const iw = W - pad.l - pad.r,
    ih = H - pad.t - pad.b;
  const maxV = Math.max(...data.map((d) => d.collected + d.overdue));
  const bw = iw / data.length - 8;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="chart-area" style={{ height }}>
        {[0, 0.5, 1].map((t) => {
          const yp = pad.t + ih * (1 - t);
          return (
            <line
              key={t}
              x1={pad.l}
              y1={yp}
              x2={W - pad.r}
              y2={yp}
              stroke="var(--border)"
              strokeWidth="1"
            />
          );
        })}
        {data.map((d, i) => {
          const bx = pad.l + i * (bw + 8) + 4;
          const ch = (d.collected / maxV) * ih;
          const oh = (d.overdue / maxV) * ih;
          const by = pad.t + ih;
          return (
            <g
              key={i}
              onMouseEnter={(e) => setTip({ d, mx: e.clientX, my: e.clientY })}
              onMouseLeave={() => setTip(null)}
              style={{ cursor: "pointer" }}
            >
              <rect
                x={bx}
                y={by - ch}
                width={bw}
                height={ch}
                rx="2"
                fill="var(--teal)"
                opacity=".75"
              />
              <rect
                x={bx}
                y={by - ch - oh}
                width={bw}
                height={oh}
                rx="2"
                fill="var(--danger)"
                opacity=".7"
              />
              <text
                x={bx + bw / 2}
                y={H - pad.b + 12}
                textAnchor="middle"
                fill="var(--text3)"
                fontSize="9"
                fontFamily="'JetBrains Mono',monospace"
              >
                {d.month}
              </text>
            </g>
          );
        })}
      </svg>
      {tip && (
        <div
          className="chart-tip"
          style={{ left: tip.mx + 12, top: tip.my - 50, position: "fixed" }}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{tip.d.month}</div>
          <div style={{ color: "var(--teal)" }}>
            Collected: ${tip.d.collected}k
          </div>
          <div style={{ color: "var(--danger)" }}>
            Overdue: ${tip.d.overdue}k
          </div>
        </div>
      )}
    </div>
  );
}

// --- Action Menu ---
function ActionMenu({ items }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) {
        return;
      }
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleToggle = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) {
        const menuWidth = 190;
        const left = Math.min(
          Math.max(rect.left, 8),
          window.innerWidth - menuWidth - 8,
        );
        setPos({ top: rect.bottom + 8, left });
      }
      setOpen((o) => !o);
    },
    [setOpen],
  );

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        className="adrop"
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          right: "auto",
          zIndex: 99999,
        }}
        onClick={(ev) => ev.stopPropagation()}
      >
        {items.map((item, i) => (
          <div
            key={i}
            className={`adrop-item ${item.danger ? "red" : ""}`}
            onClick={() => {
              setOpen(false);
              item.action?.();
            }}
          >
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </div>
        ))}
      </div>,
      document.body,
    );

  return (
    <div className="action-wrap" ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-g btn-ico btn-sm"
        onClick={handleToggle}
      >
        <i className="bi bi-three-dots" />
      </button>
      {menu}
    </div>
  );
}

export {
  Badge,
  showToast,
  Toasts,
  Modal,
  Confirm,
  RevenueChart,
  BarChart,
  DonutChart,
  CollectionBarChart,
  ActionMenu,
};
